/**
 * report/outline.ts — de paragraafboom van het rapport (featureplan §3) en welke paragrafen
 * in een rapport komen, gegeven de gekozen onderzoeken.
 *
 * De `key` is de nummering van het format (Format_bodemonderzoek.docx) en verandert nooit:
 * blokken verwijzen ernaar. Het rapport toont een eigen, doorlopende nummering: vallen
 * paragrafen weg omdat het onderzoek niet is gedaan, dan schuift de rest op zonder gaten.
 *
 * `types` is een of-lijst: de paragraaf komt erin als minstens één van die onderzoeken is
 * gekozen. Zonder `types` komt hij er altijd in. `tables` verwijst naar de tabellen in
 * docs/report-table-mapping.json ('t1' = tabel n 1, 'tPackages' = de pakkettabel).
 */

import type { InvestigationType } from './investigationTypes.js';
import type { ReportBlockType } from './types.js';

export interface OutlineNode {
  key: string;
  title: string;
  types?: InvestigationType[];
  blocks?: ReportBlockType[];
  tables?: string[];
  children?: OutlineNode[];
}

const SOIL_LIKE: InvestigationType[] = ['soil', 'groundwater', 'pfas'];
const ALL_FIELD_WORK: InvestigationType[] = ['soil', 'groundwater', 'pfas', 'asbestosSoil', 'asbestosRubble'];

/** De vijf matrices van 3.5.1 en 3.5.2: grond, grondwater, asbest bodem, puin, asfalt. */
const labSubsections = (parent: string): OutlineNode[] => [
  { key: `${parent}.1`, title: 'Grond', types: ['soil', 'pfas'], blocks: ['fixedText'] },
  { key: `${parent}.2`, title: 'Grondwater', types: ['groundwater'], blocks: ['fixedText'] },
  { key: `${parent}.3`, title: 'Asbest in bodem', types: ['asbestosSoil'], blocks: ['fixedText'] },
  { key: `${parent}.4`, title: 'Asbest in puinfundering', types: ['asbestosRubble'], blocks: ['fixedText'] },
  { key: `${parent}.5`, title: 'Asfalt', types: ['asphalt'], blocks: ['fixedText'] },
];

export const REPORT_OUTLINE: OutlineNode[] = [
  { key: '1', title: 'Samenvatting', children: [
    { key: '1.1', title: 'Onderzoeksresultaten', blocks: ['table', 'generatedText'], tables: ['t1'] },
    { key: '1.2', title: 'Aanbevelingen', children: [
      { key: '1.2.1', title: 'Bodem en grondwater', types: ['soil', 'groundwater'], blocks: ['generatedText'] },
      { key: '1.2.2', title: 'Asbest', types: ['asbestosSoil', 'asbestosRubble'], blocks: ['variantText'] },
      { key: '1.2.3', title: 'Graven en grondverzet', types: ['soil'], blocks: ['fixedText', 'table'], tables: ['t2'] },
      { key: '1.2.4', title: 'Veiligheid', types: ['crow400'], blocks: ['variantText'] },
      { key: '1.2.5', title: 'Afvoer en transport', blocks: ['fixedText'] },
    ] },
  ] },
  { key: '2', title: 'Inleiding', children: [
    { key: '2.1', title: 'Leeswijzer', blocks: ['fixedText'] },
    { key: '2.2', title: 'Doelstelling onderzoek', children: [
      { key: '2.2.1', title: 'Aanleiding', blocks: ['manualText'] },
      { key: '2.2.2', title: 'Doel verkennend milieuhygiënisch bodemonderzoek', types: SOIL_LIKE, blocks: ['fixedText'] },
      { key: '2.2.3', title: 'Doel verkennend onderzoek asbest in bodem', types: ['asbestosSoil'], blocks: ['fixedText'] },
      { key: '2.2.4', title: 'Doel verkennend onderzoek asbest in puinfundering', types: ['asbestosRubble'], blocks: ['fixedText'] },
      { key: '2.2.5', title: 'Doel asfaltonderzoek', types: ['asphalt'], blocks: ['fixedText'] },
    ] },
    { key: '2.3', title: 'Uitgangspunten', blocks: ['fixedText'] },
  ] },
  { key: '3', title: 'Onderzoeksmethode', children: [
    // TODO(open-punt-3): vooronderzoek blijft handmatig zolang er geen import is.
    { key: '3.1', title: 'Conclusie vooronderzoek', children: [
      { key: '3.1.1', title: 'Milieuhygiënische bodemkwaliteit', types: SOIL_LIKE, blocks: ['manualText', 'fixedText'] },
      { key: '3.1.2', title: 'Asbest in bodem', types: ['asbestosSoil'], blocks: ['manualText', 'fixedText'] },
      { key: '3.1.3', title: 'Asbest in puinfundering', types: ['asbestosRubble'], blocks: ['manualText', 'fixedText'] },
      { key: '3.1.4', title: 'Teerhoudendheid asfalt', types: ['asphalt'], blocks: ['manualText', 'fixedText'] },
    ] },
    { key: '3.2', title: 'Onderzoekslocatie', blocks: ['field', 'table'], tables: ['t3'] },
    { key: '3.3', title: 'Onderzoeksstrategie', children: [
      { key: '3.3.1', title: 'Bodem', types: ALL_FIELD_WORK, blocks: ['variantText', 'field', 'table'], tables: ['t4', 'tPackages'] },
      { key: '3.3.2', title: 'Asfalt', types: ['asphalt'], blocks: ['fixedText', 'table'], tables: ['t5'] },
    ] },
    { key: '3.4', title: 'Veldwerk', children: [
      { key: '3.4.1', title: 'Bemonstering grond', types: ['soil', 'pfas'], blocks: ['fixedText'] },
      { key: '3.4.2', title: 'Bemonstering asbest in bodem', types: ['asbestosSoil'], children: [
        { key: '3.4.2.1', title: 'Visuele inspectie maaiveld', blocks: ['fixedText'] },
        { key: '3.4.2.2', title: 'Proefgaten asbest', blocks: ['fixedText'] },
      ] },
      { key: '3.4.3', title: 'Bemonstering puinfundering', types: ['asbestosRubble'], blocks: ['fixedText'] },
      { key: '3.4.4', title: 'Boormethode asfalt', types: ['asphalt'], blocks: ['fixedText'] },
    ] },
    { key: '3.5', title: 'Laboratoriumonderzoek', children: [
      { key: '3.5.1', title: 'Analytisch onderzoek', children: labSubsections('3.5.1') },
      { key: '3.5.2', title: 'Interpretatie analyseresultaten', children: labSubsections('3.5.2') },
    ] },
  ] },
  { key: '4', title: 'Onderzoeksresultaten', children: [
    { key: '4.1', title: 'Uitvoer veldwerk', blocks: ['field', 'variantText'] },
    { key: '4.2', title: 'Onderzoeksresultaten bodem', types: ['soil', 'pfas'], children: [
      { key: '4.2.1', title: 'Veldwaarnemingen', blocks: ['generatedText', 'fixedText'] },
      { key: '4.2.2', title: 'Analysepakket NEN 5740 (en aanvullende parameters)', types: ['soil'], blocks: ['fixedText', 'table'], tables: ['t6'] },
      { key: '4.2.3', title: 'Analysepakket PFAS', types: ['pfas'], blocks: ['fixedText', 'table'], tables: ['t7'] },
    ] },
    { key: '4.3', title: 'Onderzoeksresultaten grondwater', types: ['groundwater'], children: [
      { key: '4.3.1', title: 'Veldmetingen grondwater', blocks: ['fixedText', 'table'], tables: ['t8'] },
      { key: '4.3.2', title: 'Analyseresultaten grondwater', blocks: ['fixedText', 'table'], tables: ['t9'] },
    ] },
    { key: '4.4', title: 'Onderzoeksresultaten asbest in bodem', types: ['asbestosSoil'], children: [
      { key: '4.4.1', title: 'Visuele inspectie maaiveld', blocks: ['manualText'] },
      { key: '4.4.2', title: 'Visuele inspectie van de opgeboorde bodem', blocks: ['generatedText'] },
      { key: '4.4.3', title: 'Schatting ruimtelijke verdeling', blocks: ['manualText'] },
      { key: '4.4.4', title: 'Gehalte asbest in bodem', blocks: ['fixedText', 'table', 'generatedText'], tables: ['t10', 't11'] },
    ] },
    { key: '4.5', title: 'Onderzoeksresultaten puinfundering', types: ['asbestosRubble'], children: [
      { key: '4.5.1', title: 'Visuele inspectie van het oppervlak', blocks: ['variantText'] },
      { key: '4.5.2', title: 'Visuele inspectie van het opgegraven materiaal', blocks: ['generatedText'] },
      { key: '4.5.3', title: 'Indeling in deellocaties', blocks: ['manualText', 'fixedText'] },
      { key: '4.5.4', title: 'Gehalte asbest in puinfundering', blocks: ['fixedText', 'table', 'generatedText'], tables: ['t12', 't13'] },
      { key: '4.5.5', title: 'Uitloging en samenstelling puinfundering', blocks: ['fixedText', 'table'], tables: ['t14'] },
    ] },
    { key: '4.6', title: 'Onderzoeksresultaten asfalt', types: ['asphalt'], children: [
      { key: '4.6.1', title: 'Veldwaarnemingen', blocks: ['fixedText', 'table'], tables: ['t15'] },
      { key: '4.6.2', title: 'Asfaltbeoordeling', blocks: ['variantText'] },
      { key: '4.6.3', title: 'GC-MS analyse', blocks: ['fixedText', 'table'], tables: ['t16'] },
    ] },
  ] },
  { key: '5', title: 'Conclusies', children: [
    { key: '5.1', title: 'Milieuhygiënische kwaliteit bodem', types: ['soil'], blocks: ['generatedText'] },
    { key: '5.2', title: 'PFAS in bodem', types: ['pfas'], blocks: ['generatedText'] },
    { key: '5.3', title: 'Grondwater', types: ['groundwater'], blocks: ['generatedText'] },
    { key: '5.4', title: 'Asbest in bodem', types: ['asbestosSoil'], blocks: ['generatedText'] },
    // Het format nummert deze twee als 5.1.1 en 5.1.2; dat is een fout in het format; ze horen onder 5.5.
    { key: '5.5', title: 'Puinfundering', types: ['asbestosRubble'], children: [
      { key: '5.5.1', title: 'Asbest in puinfundering', blocks: ['generatedText'] },
      { key: '5.5.2', title: 'Uitloging en samenstelling puinfundering', blocks: ['generatedText'] },
    ] },
    { key: '5.6', title: 'Asfalt', types: ['asphalt'], blocks: ['generatedText'] },
    { key: '5.7', title: 'CROW 400', types: ['crow400'], blocks: ['generatedText'] },
  ] },
  { key: '6', title: 'Colofon', blocks: ['field'] },
];

/** Het voorblad: alleen samenvoegvelden (featureplan §3, eerste rij). */
export const REPORT_FRONT_MATTER: OutlineNode = { key: 'cover', title: 'Voorblad', blocks: ['field'] };

/** Bijlagen 1 t/m 7 uit het format. Ze worden genummerd zoals ze in dit rapport voorkomen. */
export const REPORT_APPENDICES: OutlineNode[] = [
  { key: 'appendix.samplingPlan', title: 'Monsterplan', blocks: ['attachment'] },
  { key: 'appendix.boreholeLogs', title: 'Boorprofielen', blocks: ['attachment'] },
  { key: 'appendix.labCertificates', title: 'Analysecertificaten', blocks: ['attachment'] },
  { key: 'appendix.assessments', title: 'Toetsingen', types: ['soil', 'groundwater', 'pfas', 'asbestosRubble'], blocks: ['attachment'] },
  { key: 'appendix.preliminaryInvestigation', title: 'Vooronderzoek', blocks: ['attachment'] },
  { key: 'appendix.principles', title: 'Uitgangspunten', blocks: ['attachment'] },
  { key: 'appendix.crow400', title: 'Toetsing CROW 400', types: ['crow400'], blocks: ['attachment'] },
];

/** Een paragraaf in een concreet rapport: met de nummering die het rapport toont. */
export interface ReportOutlineNode {
  key: string;
  number: string;
  title: string;
  depth: number;
  blocks: ReportBlockType[];
  tables: string[];
  children: ReportOutlineNode[];
}

export interface ReportOutline {
  frontMatter: ReportOutlineNode;
  chapters: ReportOutlineNode[];
  appendices: ReportOutlineNode[];
}

function applies(node: OutlineNode, chosen: ReadonlySet<string>): boolean {
  return !node.types || node.types.some((t) => chosen.has(t));
}

function build(nodes: OutlineNode[], chosen: ReadonlySet<string>, prefix: string, depth: number): ReportOutlineNode[] {
  const out: ReportOutlineNode[] = [];
  for (const node of nodes) {
    if (!applies(node, chosen)) continue;
    const number = prefix ? `${prefix}.${out.length + 1}` : String(out.length + 1);
    const children = build(node.children || [], chosen, number, depth + 1);
    const blocks = node.blocks || [];
    // Een tussenkop zonder eigen inhoud waarvan alle paragrafen zijn weggevallen, valt ook weg.
    if (!blocks.length && node.children?.length && !children.length) continue;
    out.push({ key: node.key, number, title: node.title, depth, blocks: [...blocks], tables: [...(node.tables || [])], children });
  }
  return out;
}

/**
 * De paragrafen van een rapport met deze onderzoeken, doorlopend genummerd. Onbekende
 * onderzoekssleutels worden genegeerd.
 */
export function buildReportOutline(investigationTypes: readonly string[]): ReportOutline {
  const chosen = new Set(investigationTypes || []);
  return {
    frontMatter: { key: REPORT_FRONT_MATTER.key, number: '', title: REPORT_FRONT_MATTER.title, depth: 0,
      blocks: [...(REPORT_FRONT_MATTER.blocks || [])], tables: [], children: [] },
    chapters: build(REPORT_OUTLINE, chosen, '', 1),
    appendices: REPORT_APPENDICES.filter((a) => applies(a, chosen)).map((a, i) => ({
      key: a.key, number: `Bijlage ${i + 1}`, title: a.title, depth: 1,
      blocks: [...(a.blocks || [])], tables: [], children: [],
    })),
  };
}

/** Alle paragrafen van een rapport op een rij, in leesvolgorde. */
export function flattenOutline(nodes: ReportOutlineNode[]): ReportOutlineNode[] {
  return nodes.flatMap((n) => [n, ...flattenOutline(n.children)]);
}
